/**
 * CouponCode Component
 *
 * A reusable component for displaying coupon codes with copy-to-clipboard functionality.
 * Used in both coupons-list and spend-rules-list blocks.
 */
import React, { useState, type FC } from 'react';
import { __ } from '@wordpress/i18n';
import Button from '../button';
import './coupon-code.scss';

// ============================================================================
// Types
// ============================================================================

export interface CouponCodeProps {
	/** The coupon code to display */
	code: string;
	/** Optional label text (defaults to "Code:") */
	label?: string;
	/** Optional className for styling */
	className?: string;
	/** Callback fired when code is copied */
	onCopy?: () => void;
	/** Optional button styles from editor controls */
	buttonStyles?: React.CSSProperties;
	/** Callback fired when apply button is clicked */
	onApply?: () => void;
	/** Callback fired when remove button is clicked */
	onRemove?: () => void;
	/** Whether the coupon is currently applied */
	isApplied?: boolean;
	/** Whether an apply/remove operation is in progress */
	isLoading?: boolean;
	/** Error message to display */
	error?: string | null;
}

// ============================================================================
// Component
// ============================================================================

/**
 * CouponCode Component
 *
 * Displays a coupon code with either:
 * - A copy-to-clipboard button (default behavior)
 * - Apply/Remove buttons for cart integration (when onApply is provided)
 *
 * Provides visual feedback for all interactions.
 */
export const CouponCode: FC<CouponCodeProps> = ({
	code,
	label = __('Code:', 'leat-crm'),
	className = '',
	onCopy,
	buttonStyles,
	onApply,
	onRemove,
	isApplied = false,
	isLoading = false,
	error = null,
}) => {
	const [copied, setCopied] = useState(false);

	const handleCopyCode = async () => {
		try {
			// Try modern clipboard API first
			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(code);
			} else {
				// Fallback for older browsers
				const textarea = document.createElement('textarea');
				textarea.value = code;
				textarea.style.position = 'fixed';
				textarea.style.opacity = '0';
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand('copy');
				document.body.removeChild(textarea);
			}

			setCopied(true);

			// Call optional callback
			if (onCopy) {
				onCopy();
			}

			// Reset copied state after 2 seconds
			setTimeout(() => {
				setCopied(false);
			}, 2000);
		} catch (err) {
			console.error('Failed to copy coupon code:', err);
			// Still show copied state even if copy failed
			// User will at least see visual feedback
			setCopied(true);
			setTimeout(() => {
				setCopied(false);
			}, 2000);
		}
	};

	// If onApply is provided, show apply/remove functionality
	if (onApply) {
		return (
			<div className={`leat-coupon-code leat-coupon-code--apply-mode ${className}`.trim()}>
				<div className="leat-coupon-code__code-section">
					<code className="leat-coupon-code__value">{code}</code>
				</div>

				{/* Apply/Remove buttons */}
				{!isApplied ? (
					<Button
						variant="contained"
						className="leat-button--small"
						onClick={onApply}
						disabled={isLoading}
						style={buttonStyles}
					>
						{isLoading ? __('Applying...', 'leat-crm') : __('Apply', 'leat-crm')}
					</Button>
				) : (
					<div className="leat-coupon-code__split-button">
						<Button
							variant="contained"
							className="leat-button--small leat-coupon-code__applied-button"
							disabled
							style={buttonStyles}
						>
							<CheckIcon />
							<span className="leat-sr-only">{__('Applied', 'leat-crm')}</span>
						</Button>
						{onRemove && (
							<button
								className="leat-coupon-code__remove-button"
								onClick={onRemove}
								disabled={isLoading}
								aria-label={__('Remove coupon', 'leat-crm')}
								style={{
									backgroundColor: buttonStyles?.backgroundColor,
									borderColor: buttonStyles?.backgroundColor,
								}}
							>
								<XIcon />
							</button>
						)}
					</div>
				)}
			</div>
		);
	}

	// Default behavior: show copy button
	return (
		<div className={`leat-coupon-code ${className}`.trim()}>
			<div className="leat-coupon-code__code-section">
				<code className="leat-coupon-code__value">{code}</code>
			</div>
			<Button
				variant="contained"
				className="leat-button--small"
				onClick={handleCopyCode}
				title={copied ? __('Copied!', 'leat-crm') : __('Copy code', 'leat-crm')}
				aria-label={copied ? __('Copied!', 'leat-crm') : __('Copy code', 'leat-crm')}
				style={buttonStyles}
			>
				{copied ? __('Copied!', 'leat-crm') : __('Copy Code', 'leat-crm')}
			</Button>
		</div>
	);
};

// ============================================================================
// Icons
// ============================================================================

const CopyIcon: FC = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		aria-hidden="true"
	>
		<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
		<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
	</svg>
);

const CheckIcon: FC = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<polyline points="20 6 9 17 4 12" />
	</svg>
);

const XIcon: FC = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);
